<template>
  <!-- EN-1516: queued download commands for the AGMs in the user's group (EN-1551: downloads only). -->
  <div class="column" data-test="queued-commands-list">
    <div class="row items-center no-wrap q-mb-xs">
      <div class="text-bold col">Queued Download Commands</div>
      <q-btn
        flat
        dense
        round
        size="sm"
        icon="refresh"
        :loading="isLoading"
        data-test="queued-commands-refresh"
        @click="fetchQueuedCommands"
      >
        <q-tooltip>Refresh</q-tooltip>
      </q-btn>
    </div>

    <div v-if="errorMessage" class="text-negative text-caption q-mb-xs">
      {{ errorMessage }}
    </div>
    <div
      v-else-if="!isLoading && commands.length === 0"
      class="text-grey-7 text-caption"
    >
      There are no queued download commands.
    </div>

    <!-- Client-side filter on the loaded rows (frontend only). -->
    <template v-if="commands.length > 0 || uidFilterText">
      <q-input
        v-model="uidFilterText"
        dense
        outlined
        clearable
        autogrow
        type="textarea"
        color="blue-brand"
        label="AGM UIDs"
        hint="One or more, separated by commas, spaces or new lines"
        class="q-mb-sm"
        data-test="queued-commands-uid-filter"
        @clear="clearUidFilter"
      >
        <template #prepend>
          <q-icon name="search" />
        </template>
      </q-input>
      <div
        v-if="isUidFilterActive && filteredCommands.length > 0"
        class="text-grey-7 text-caption q-mb-xs"
        data-test="queued-commands-uid-filter-count"
      >
        Showing {{ filteredCommands.length }} of {{ commands.length }}
      </div>
      <div
        v-if="
          isUidFilterActive &&
          commands.length > 0 &&
          filteredCommands.length === 0
        "
        class="row items-center no-wrap text-grey-7 text-caption q-mb-xs"
        data-test="queued-commands-uid-filter-empty"
      >
        <span class="col">No queued commands match these AGM UIDs.</span>
        <q-btn
          flat
          dense
          no-caps
          size="sm"
          color="blue-brand"
          label="Clear filter"
          @click="clearUidFilter"
        />
      </div>
    </template>

    <q-list
      v-if="filteredCommands.length > 0"
      dense
      bordered
      separator
      class="rounded-borders"
      style="max-height: 320px; overflow-y: auto"
    >
      <q-item
        v-for="command in filteredCommands"
        :key="command.commandId"
        :data-test="'queued-command-' + command.commandId"
      >
        <q-item-section>
          <q-item-label>
            <span class="text-weight-medium">UID {{ command.uid }}</span>
            · {{ typeLabel(command) }}
            <q-badge
              v-if="command.transport === 'Iridium'"
              outline
              color="grey-7"
              label="Iridium"
              class="q-ml-xs"
            />
          </q-item-label>
          <q-item-label v-if="rangeText(command)" caption>
            {{ rangeText(command) }} UTC
          </q-item-label>
          <q-item-label
            v-if="isAdmin && command.isDownload && !canEdit(command)"
            caption
            class="text-grey-7"
            :data-test="'queued-command-edit-reason-' + command.commandId"
          >
            {{ editReason(command) }}
          </q-item-label>
          <q-item-label caption>
            <q-badge
              :color="statusColor(command)"
              :label="statusLabel(command)"
            />
            <span class="q-ml-xs">queued {{ queuedAt(command) }} UTC</span>
          </q-item-label>
        </q-item-section>

        <q-item-section side>
          <div class="row no-wrap items-center">
            <q-btn
              v-if="command.downloadSessionId && canShowSessions"
              flat
              dense
              round
              size="sm"
              icon="visibility"
              @click="onShowSessionClick(command)"
            >
              <q-tooltip>Show download session</q-tooltip>
            </q-btn>
            <template v-if="isAdmin">
              <!-- The span takes the hover: a disabled q-btn gets none. On
                   touch, the reason is also the caption line above. -->
              <span v-if="command.isDownload" class="inline-block">
                <q-btn
                  flat
                  dense
                  round
                  size="sm"
                  icon="edit"
                  :disable="!canEdit(command)"
                  :data-test="'queued-command-edit-' + command.commandId"
                  @click="onEditClick(command)"
                />
                <q-tooltip>{{
                  canEdit(command) ? 'Edit range' : editReason(command)
                }}</q-tooltip>
              </span>
              <q-btn
                v-if="canCancel(command)"
                flat
                dense
                round
                size="sm"
                icon="cancel"
                color="negative"
                :loading="cancellingId === command.commandId"
                :data-test="'queued-command-cancel-' + command.commandId"
                @click="onCancelClick(command)"
              >
                <q-tooltip>Cancel download</q-tooltip>
              </q-btn>
            </template>
          </div>
        </q-item-section>
      </q-item>
    </q-list>

    <EditQueuedRangeDialog
      v-model="showEditDialog"
      :command="editingCommand"
      :firmware-version="editingFirmwareVersion"
      @saved="onEditSaved"
      @stale="fetchQueuedCommands"
    />

    <!-- Cancel confirmation. -->
    <q-dialog v-model="showCancelConfirm">
      <q-card style="width: 420px; max-width: 90vw">
        <q-card-section class="text-bold">Cancel download?</q-card-section>
        <q-card-section v-if="cancellingCommand" class="q-pt-none">
          {{ confirmText(cancellingCommand) }}
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat no-caps label="Keep" />
          <q-btn
            class="btn-brand"
            no-caps
            label="Cancel download"
            data-test="queued-command-cancel-confirm"
            @click="confirmCancel"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Result / error messages. -->
    <q-dialog v-model="showMessageDialog">
      <q-card style="width: 420px; max-width: 90vw">
        <q-card-section class="text-bold">{{ messageTitle }}</q-card-section>
        <q-card-section class="q-pt-none">{{ messageText }}</q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat no-caps label="OK" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import CommandService from '@/services/CommandService';
import EditQueuedRangeDialog from '@/components/EditQueuedRangeDialog.vue';
import { Device } from '@/models/Device';
import { IQueuedCommand } from '@/models/QueuedCommand';
import {
  LatestRequestGate,
  NO_GROUP_MESSAGE,
  QUEUED_COMMANDS_POLICY,
  QUEUED_COMMANDS_POLL_MS,
  cancelConfirmText,
  cancelErrorMessage,
  canCancelQueuedCommand,
  canEditQueuedCommand,
  canManageQueuedCommands,
  cancelResultSummary,
  editUnavailableReason,
  onlyDownloadCommands,
  queuedAtText,
  queuedCommandRangeText,
  queuedCommandStatusColor,
  queuedCommandStatusLabel,
  queuedCommandTypeLabel,
  sortQueuedCommands,
} from '@/utils/QueuedCommandDisplay';
import { filterByAgmUids, parseAgmUidFilter } from '@/utils/AgmUidFilter';

/** Refresh at most this often when download session updates arrive. */
const SESSION_REFRESH_DEBOUNCE_MS = 2000;

export default defineComponent({
  name: 'QueuedCommandsList',
  components: { EditQueuedRangeDialog },
  props: {
    /** Fetch while true (the Download Manager is open). */
    active: { type: Boolean, default: false },
    /** Show the per-row "Show download session" button (EN-1517 present). */
    canShowSessions: { type: Boolean, default: false },
  },
  emits: ['show-sessions'],
  data() {
    return {
      commands: [] as IQueuedCommand[],
      isLoading: false,
      errorMessage: '',
      showEditDialog: false,
      editingCommand: null as IQueuedCommand | null,
      showCancelConfirm: false,
      cancellingCommand: null as IQueuedCommand | null,
      cancellingId: null as number | null,
      showMessageDialog: false,
      messageTitle: '',
      messageText: '',
      sessionRefreshTimer: null as ReturnType<typeof setTimeout> | null,
      pollTimer: null as ReturnType<typeof setInterval> | null,
      loadGate: new LatestRequestGate(),
      /** "AGM UIDs" filter text; filters the loaded rows only. */
      uidFilterText: '' as string | null,
    };
  },
  computed: {
    isAdmin(): boolean {
      return canManageQueuedCommands(this.$store.state.user);
    },
    isUidFilterActive(): boolean {
      return parseAgmUidFilter(this.uidFilterText).length > 0;
    },
    /** The loaded rows the "AGM UIDs" filter lets through (all when empty). */
    filteredCommands(): IQueuedCommand[] {
      return filterByAgmUids(this.commands, this.uidFilterText, (c) => c.uid);
    },
    editingFirmwareVersion(): number | undefined {
      const uid = this.editingCommand?.uid;
      const devices: Device[] = this.$store.state.device?.devices || [];
      return devices.find((d) => d.uid === uid)?.firmwareVersion;
    },
    /** Changes whenever a download session is created or changes state. */
    downloadSessionSignature(): string {
      const sessions: any[] =
        this.$store.getters.activeDownloadSessionList || [];
      return sessions.map((s) => s.downloadSessionId + ':' + s.state).join(',');
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(active: boolean) {
        if (active) {
          this.fetchQueuedCommands();
          this.startPolling();
        } else {
          this.stopPolling();
        }
      },
    },
    downloadSessionSignature() {
      if (!this.active) return;
      if (this.sessionRefreshTimer) clearTimeout(this.sessionRefreshTimer);
      this.sessionRefreshTimer = setTimeout(() => {
        this.sessionRefreshTimer = null;
        this.fetchQueuedCommands();
      }, SESSION_REFRESH_DEBOUNCE_MS);
    },
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    typeLabel: queuedCommandTypeLabel,
    rangeText: queuedCommandRangeText,
    queuedAt: queuedAtText,
    statusLabel: queuedCommandStatusLabel,
    statusColor: queuedCommandStatusColor,
    editReason: editUnavailableReason,
    confirmText: cancelConfirmText,
    // 15 s auto-refresh while the list is visible; there is no CommandQueue
    // hub event, so Waiting -> Queued -> processed shows up this way.
    startPolling() {
      if (this.pollTimer) return;
      this.pollTimer = setInterval(() => {
        if (!this.active || this.showEditDialog || this.cancellingId !== null)
          return;
        this.fetchQueuedCommands();
      }, QUEUED_COMMANDS_POLL_MS);
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
      if (this.sessionRefreshTimer) clearTimeout(this.sessionRefreshTimer);
      this.sessionRefreshTimer = null;
      this.loadGate.invalidate();
      this.isLoading = false;
    },
    clearUidFilter() {
      this.uidFilterText = '';
    },
    canEdit(command: IQueuedCommand): boolean {
      return canEditQueuedCommand(command, this.isAdmin);
    },
    canCancel(command: IQueuedCommand): boolean {
      return canCancelQueuedCommand(command, this.isAdmin);
    },
    // Newest request wins: a load asked for while one is in flight runs
    // right after it, and a response that is no longer the latest is ignored.
    async fetchQueuedCommands() {
      if (this.loadGate.inFlight) {
        this.loadGate.requestAgain();
        return;
      }
      const requestId = this.loadGate.begin();
      this.isLoading = true;
      try {
        const response = await CommandService.getQueuedCommands({
          includeStopRows: QUEUED_COMMANDS_POLICY.showStopRows,
        });
        if (!this.loadGate.isLatest(requestId)) return;
        // EN-1551: downloads only; other queued commands are not listed.
        this.commands = sortQueuedCommands(
          onlyDownloadCommands(response.data || [])
        );
        // EN-1557: the Download Sessions card counts follow this list.
        this.$store.dispatch('queuedDownloadCommandsLoaded', this.commands);
        this.errorMessage = '';
      } catch (exception: any) {
        if (!this.loadGate.isLatest(requestId)) return;
        this.commands = [];
        this.errorMessage =
          exception?.response?.status === 403
            ? NO_GROUP_MESSAGE
            : 'The queued commands could not be loaded.';
      } finally {
        if (this.loadGate.end(requestId)) {
          this.isLoading = false;
          if (this.loadGate.takeQueued() && this.active)
            this.fetchQueuedCommands();
        }
      }
    },
    onShowSessionClick(command: IQueuedCommand) {
      this.$emit('show-sessions', {
        uid: command.uid,
        downloadSessionId: command.downloadSessionId,
        commandId: command.commandId,
      });
    },
    onEditClick(command: IQueuedCommand) {
      this.editingCommand = command;
      this.showEditDialog = true;
    },
    onEditSaved(updated: IQueuedCommand) {
      const index = this.commands.findIndex(
        (c) => c.commandId === updated?.commandId
      );
      if (index >= 0) this.commands.splice(index, 1, updated);
      // Always refetch after a save: drop any older in-flight response (it
      // could overwrite the updated row) and load again now.
      this.loadGate.invalidate();
      this.isLoading = false;
      this.fetchQueuedCommands();
    },
    onCancelClick(command: IQueuedCommand) {
      this.cancellingCommand = command;
      this.showCancelConfirm = true;
    },
    showMessage(title: string, text: string) {
      this.messageTitle = title;
      this.messageText = text;
      this.showMessageDialog = true;
    },
    async confirmCancel() {
      const command = this.cancellingCommand;
      this.showCancelConfirm = false;
      if (!command) return;
      this.cancellingId = command.commandId;
      try {
        // TODO(EN-1515): use DeviceService.cancelDownloadCommands once on main.
        const response = await CommandService.cancelDownloadCommandsAdapter({
          commandIds: [command.commandId],
        });
        this.showMessage('Cancel download', cancelResultSummary(response.data));
      } catch (exception: any) {
        this.showMessage(
          'Cancel download',
          cancelErrorMessage(
            exception?.response?.status,
            exception?.response?.data
          )
        );
      } finally {
        this.cancellingId = null;
        this.cancellingCommand = null;
        await this.fetchQueuedCommands();
      }
    },
  },
});
</script>
